/**
 * Startup warmup: pre-fill the caches of the most-visited pages right after
 * authentication, so the user's FIRST click on Clients / Tâches / Compta /
 * Trésorerie paints instantly instead of showing skeletons for a full API
 * round-trip. Together with the persisted cache (lib/query-persist.ts) this
 * removes the "every page loads in front of me" feeling on a slow API.
 *
 * Design:
 *  - STAGGERED (400 ms apart) so the burst never hammers the API/DB pool —
 *    the backend answers the on-screen page first, then the warmup fills in.
 *  - `prefetchQuery` respects `staleTime`: anything already fresh (just
 *    hydrated from the persisted snapshot, or already visited) is skipped.
 *  - Failures are ignored — a warmup miss just means that page loads the
 *    normal way when visited.
 *  - Authenticated sessions only.
 */

import { useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/features/auth/auth.store";
import { prefetchAccountingOverview } from "@/features/accounting/accounting.api";
import { prefetchClients } from "@/features/clients/clients.hooks";
import { prefetchDashboard } from "@/features/dashboard/dashboard.hooks";
import { prefetchTasks } from "@/features/tasks/tasks.hooks";
import { prefetchTreasury } from "@/features/treasury/treasury.hooks";

/** Most-visited surfaces, in priority order (landing page first). */
const WARMUP_STEPS: ReadonlyArray<(qc: QueryClient) => Promise<void>> = [
  prefetchDashboard,
  prefetchClients,
  prefetchTasks,
  prefetchAccountingOverview,
  prefetchTreasury,
];

const STAGGER_MS = 400;

export function useWarmupCoreData(): void {
  const qc = useQueryClient();
  const authed = useAuthStore((s) => s.status === "authenticated");
  const started = useRef(false);

  useEffect(() => {
    if (!authed || started.current) return;
    started.current = true; // once per shell mount (a re-login remounts the shell)
    const timers = WARMUP_STEPS.map((run, i) =>
      setTimeout(() => {
        void run(qc).catch(() => undefined);
      }, STAGGER_MS * (i + 1)),
    );
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [authed, qc]);
}
